import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-hot-toast";
import { ShoppingCartIcon, TrashIcon } from "@heroicons/react/24/solid";

import Button from "src/common/buttons/Button";
import ButtonGroup from "src/common/buttons/ButtonGroup";
import DialogOverlay from "src/common/DialogOverlay";

import { apiInstance } from "src/services/api";
import { useAccount } from "src/AccountContext";
import { parseAccountParams, parseAxiosErrors } from "src/utils/Parse";

interface FBACartButtonProps {
  integrationConfigId: string | undefined;
}

interface FBAItem {
  amazonSku: string;
  quantity: number;
}

interface FBAData {
  integrationConfigId: number;
  items: FBAItem[];
}

export function FBACartButton({ integrationConfigId }: FBACartButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<FBAItem[]>([]);
  const { account } = useAccount();
  const params = parseAccountParams(account);

  // Load items from localStorage
  const loadItems = () => {
    if (!integrationConfigId) return;

    const storageKey = `fbaItems_${integrationConfigId}`;
    const existingData = localStorage.getItem(storageKey);

    if (existingData) {
      const fbaData: FBAData = JSON.parse(existingData);
      setItems(fbaData.items || []);
    } else {
      setItems([]);
    }
  };

  // Poll localStorage for changes
  useEffect(() => {
    loadItems();

    const interval = setInterval(() => {
      loadItems();
    }, 1000); // Check every second for updates

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationConfigId]);

  const handleDelete = (amazonSku: string) => {
    if (!integrationConfigId) return;

    const storageKey = `fbaItems_${integrationConfigId}`;
    const existingData = localStorage.getItem(storageKey);

    if (existingData) {
      const fbaData: FBAData = JSON.parse(existingData);
      fbaData.items = fbaData.items.filter(
        (item) => item.amazonSku !== amazonSku
      );
      localStorage.setItem(storageKey, JSON.stringify(fbaData));
      setItems(fbaData.items);

      toast.success("Item removed from cart");
    }
  };

  const handleSubmit = async () => {
    if (!integrationConfigId || items.length === 0) {
      toast.error("No items in cart");
      return;
    }

    setLoading(true);

    try {
      const response = apiInstance.post(
        `/orders/FbaPrep`,
        {
          integrationConfigId: Number(integrationConfigId),
          items: items,
        },
        { params }
      );

      await toast.promise(response, {
        loading: "Submitting FBA prep order...",
        success: "FBA prep order created successfully",
        error: (err) => parseAxiosErrors(err),
      });

      // Clear localStorage after successful submission
      resetOrder();
      setItems([]);

      setIsOpen(false);
    } catch (error) {
      console.error("Failed to submit FBA prep order:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetOrder = () => {
    // Clear localStorage after successful submission
    const storageKey = `fbaItems_${integrationConfigId}`;
    localStorage.removeItem(storageKey);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const totalUnits = items.reduce(
    (sum, item) => sum + Number(item.quantity),
    0
  );
  const totalSkus = items.length;

  if (!integrationConfigId) {
    return null;
  }

  return (
    <>
      <Button
        label={`FBA Cart ${
          totalSkus > 0 ? `(${totalSkus} SKUs, ${totalUnits} Units)` : ""
        }`}
        design="primary"
        icon={<ShoppingCartIcon className="mr-2 h-5 w-5" />}
        func={openModal}
        disabled={items.length === 0}
      />

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          open={isOpen}
          onClose={closeModal}
        >
          <DialogOverlay />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    FBA Prep Order Cart
                    <p className="text-sm font-light text-gray-500 mt-2">
                      Review your items before submitting to create an FBA prep
                      order.
                    </p>
                  </Dialog.Title>

                  <div className="mt-4">
                    {items.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <ShoppingCartIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                        <p>Your cart is empty</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                          <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                              <tr>
                                <th
                                  scope="col"
                                  className="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                >
                                  Amazon SKU
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                >
                                  Quantity
                                </th>
                                <th
                                  scope="col"
                                  className="relative py-3 pl-3 pr-4"
                                >
                                  <span className="sr-only">Delete</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {items.map((item, index) => (
                                <tr key={index}>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                    {item.amazonSku}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {item.quantity}
                                  </td>
                                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                                    <button
                                      onClick={() =>
                                        handleDelete(item.amazonSku)
                                      }
                                      className="text-red-600 hover:text-red-900 inline-flex items-center"
                                    >
                                      <TrashIcon className="h-4 w-4 mr-1" />
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="flex justify-end pt-2 text-sm font-medium text-gray-900">
                          {totalSkus} SKUs, {totalUnits} Total Units
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <ButtonGroup>
                      <Button
                        label="Delete All Items"
                        design="delete"
                        func={resetOrder}
                      />
                      <Button
                        label="Cancel"
                        design="secondary"
                        func={closeModal}
                      />
                      <Button
                        label="Submit FBA Prep Order"
                        design="primary"
                        func={handleSubmit}
                        loading={loading}
                        disabled={items.length === 0}
                      />
                    </ButtonGroup>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
